import type { ClientMessage, ServerMessage } from '../types/protocol';

export type WebSocketStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface WebSocketClientOptions {
  url: string;
  onMessage: (message: ServerMessage) => void;
  onStatusChange: (status: WebSocketStatus) => void;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private readonly options: Required<WebSocketClientOptions>;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionalClose = false;

  constructor(options: WebSocketClientOptions) {
    this.options = {
      reconnectInterval: 3000,
      maxReconnectAttempts: 10,
      ...options,
    };
  }

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      return;
    }

    this.intentionalClose = false;
    this.options.onStatusChange('connecting');

    try {
      // Resolve the WebSocket URL:
      // - Relative paths (e.g. "/ws") become wss://currentHost/ws on HTTPS pages
      // - ws:// auto-upgrades to wss:// on HTTPS pages (except localhost,
      //   which browsers treat as a secure context allowing ws:// from HTTPS)
      let url = this.options.url;
      if (typeof location !== 'undefined') {
        if (!url.startsWith('ws://') && !url.startsWith('wss://')) {
          const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
          url = `${proto}//${location.host}${url.startsWith('/') ? '' : '/'}${url}`;
        } else if (location.protocol === 'https:' && url.startsWith('ws://')) {
          const wsHost = new URL(url).hostname;
          if (wsHost !== 'localhost' && wsHost !== '127.0.0.1' && wsHost !== '::1') {
            url = 'wss://' + url.slice(5);
          }
        }
      }
      this.ws = new WebSocket(url);
    } catch {
      this.options.onStatusChange('error');
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.options.onStatusChange('connected');
    };

    this.ws.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(String(event.data)) as ServerMessage;
        this.options.onMessage(data);
      } catch {
        // Ignore unparseable messages
      }
    };

    this.ws.onclose = () => {
      this.ws = null;
      if (!this.intentionalClose) {
        this.options.onStatusChange('disconnected');
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = () => {
      this.options.onStatusChange('error');
    };
  }

  send(message: ClientMessage): void {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      return;
    }
    this.ws.send(JSON.stringify(message));
  }

  disconnect(): void {
    this.intentionalClose = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.options.onStatusChange('disconnected');
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.options.maxReconnectAttempts) {
      this.options.onStatusChange('error');
      return;
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect();
    }, this.options.reconnectInterval);
  }
}
